import React, { useState } from "react";
import {
    CreditCard, ShoppingCart, Loader2, ArrowRight, ShieldCheck,
    MessageCircle, Facebook, MapPin, Navigation, CheckCircle, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

import { useCart } from "@/context/CartContext";

export const PaymentModal = ({ product, onClose }) => {
    const { addToCart } = useCart();
    const buyerData = JSON.parse(localStorage.getItem("buyer_user") || "{}");
    const token = localStorage.getItem("buyer_token");
    const [loading, setLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(buyerData.phone || "");
    const [qty, setQty] = useState(product.quantity || 1);
    const [deliveryType, setDeliveryType] = useState("pickup");
    const [paymentMethod, setPaymentMethod] = useState("moncash");
    const [bvgpayBalance, setBvgpayBalance] = useState(null);
    const [address, setAddress] = useState("");
    const [coords, setCoords] = useState(null);
    const [proposedFee, setProposedFee] = useState("");
    const [deliveryVehicle, setDeliveryVehicle] = useState("moto");
    const [calculatedKm, setCalculatedKm] = useState(null);
    const [selectedImageIdx, setSelectedImageIdx] = useState(0);
    const [buyForSomeone, setBuyForSomeone] = useState(false);
    const [recipientName, setRecipientName] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("");
    const [onlineDrivers, setOnlineDrivers] = useState([]);
    const [showDriversList, setShowDriversList] = useState(false);

    const fetchOnlineDrivers = async () => {
        try {
            const res = await axios.get(`${API}/drivers/list-active?_t=${Date.now()}`);
            if (res.data?.success) {
                setOnlineDrivers(res.data.drivers || []);
            }
        } catch (e) {}
    };

    React.useEffect(() => {
        fetchOnlineDrivers();
        const interval = setInterval(fetchOnlineDrivers, 10000);
        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        if (token) {
            axios.get(`${API}/linkamazon/bvgpay/wallet`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => setBvgpayBalance(res.data.balance_htg))
                .catch(() => {});
        }
    }, [token]);

    const validImages = product.images?.filter(img => img) || [];
    if(product.image_url && validImages.length === 0) validImages.push(product.image_url);
    const productImage = validImages[selectedImageIdx] || null;

    const shareUrl = `${window.location.origin}/marketplace?product=${product.id}`;
    const productName = product.name || product.title || "Pwodwi";
    const shareText = `Tcheke bèl pwodwi sa a nan BVGLink Marketplace: ${productName} - ${product.price_htg} HTG`;

    const handleShare = (platform) => {
        let url = "";
        switch (platform) {
            case "whatsapp":
                url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
                break;
            case "facebook":
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                break;
            default:
                break;
        }
        if (url) window.open(url, "_blank");
    };

    const haversineKm = (lat1, lon1, lat2, lon2) => {
        const R = 6371; 
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        return R * c;
    };

    const computeFee = (km, vehicle) => {
        const rate = vehicle === 'machine' ? 50 : 25; // 25 HTG/KM for moto, 50 HTG/KM for machine
        const minFee = vehicle === 'machine' ? 50 : 25;
        const raw = Math.ceil(km * rate);
        return Math.max(minFee, raw);
    };

    const handleVehicleChange = (newVehicle) => {
        setDeliveryVehicle(newVehicle);
        const km = calculatedKm || 2.0;
        const fee = computeFee(km, newVehicle);
        setProposedFee(fee.toString());
    };

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Navigatè w la pa sipòte GPS.");
            return;
        }

        toast.info("N'ap chache pozisyon w...");
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCoords({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                });
                
                let sellerLoc = product.seller_location;
                let distKm = 2.0;
                
                if (sellerLoc && sellerLoc.lat && sellerLoc.lng) {
                    distKm = haversineKm(sellerLoc.lat, sellerLoc.lng, position.coords.latitude, position.coords.longitude);
                    distKm = Math.max(0.5, distKm);
                    toast.success(`Distans kalkile: ${distKm.toFixed(1)} KM`);
                } else {
                    distKm = 2.0;
                    toast.info("Lokalizasyon anrejistre! (Estimasyon baz: 2 KM)");
                }

                setCalculatedKm(distKm);
                const fee = computeFee(distKm, deliveryVehicle);
                setProposedFee(fee.toString());
                toast.success("Mwen jwenn lokalizasyon w! ✅");
            },
            (error) => {
                toast.error("Mwen pa ka jwenn lokalizasyon w. Tcheke si GPS la aktive.");
                const distKm = 2.0;
                setCalculatedKm(distKm);
                const fee = computeFee(distKm, deliveryVehicle);
                setProposedFee(fee.toString());
            }
        );
    };

    const handleCreateOrder = async () => {
        if (!phoneNumber || phoneNumber.length < 8) {
            toast.error(paymentMethod === "moncash" ? "Tanpri antre yon nimewo MonCash valid." : "Tanpri antre yon nimewo telefòn valid.");
            return;
        }

        if (buyForSomeone) {
            if (!recipientName || recipientName.length < 2) {
                toast.error("Tanpri mete non moun k'ap resevwa a kòrèkteman.");
                return;
            }
            if (!recipientPhone || recipientPhone.length < 8) {
                toast.error("Tanpri mete nimewo telefòn moun k'ap resevwa a.");
                return;
            }
        }

        if (deliveryType === "delivery") {
            if (!address) {
                toast.error("Tanpri antre adrès livrezon an.");
                return;
            }
            if (!proposedFee || proposedFee <= 0) {
                toast.error("Tanpri klike sou 'Voye pozisyon m' pou kalkile pri livrezon an.");
                return;
            }
        }

        setLoading(true);
        try {
            const isAutoCalculated = !!(product.seller_location?.lat && coords && deliveryType === 'delivery');

            // New Flow: Create Order first
            const res = await axios.post(`${API}/marketplace/create-order`, {
                product_id: product.id,
                buyer_phone: phoneNumber,
                buyer_email: buyerData.email,
                quantity: qty,
                delivery_type: deliveryType,
                delivery_address: address,
                proposed_delivery_fee: parseFloat(proposedFee) || 0,
                location: coords,
                recipient_name: buyForSomeone ? recipientName : null,
                recipient_phone: buyForSomeone ? recipientPhone : null,
                skip_bidding: true // Always skip bidding since fee is auto-calculated
            });

            if (res.data.success) {
                const orderId = res.data.order_id;
                
                if (deliveryType === "pickup" || deliveryType === "delivery") {
                    // Proceed to immediate payment for both pickup and automatic delivery
                    if (paymentMethod === "bvgpay") {
                        const payRes = await axios.post(`${API}/marketplace/orders/${orderId}/pay-bvgpay`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (payRes.data.success) {
                            toast.success("Acha a fèt avèk siksè kounye a!");
                            setTimeout(() => {
                                onClose();
                                window.location.href = `/order/${orderId}`;
                            }, 1500);
                        }
                    } else {
                        const payRes = await axios.post(`${API}/marketplace/create-payment/${orderId}/pay-moncash`);
                        if (payRes.data.redirect_url) {
                            window.location.href = payRes.data.redirect_url;
                        } else {
                            toast.error("Erè nan kòmanse peman an.");
                        }
                    }
                } else {
                    // Delivery: Negotiation starts
                    toast.success("Demann livrezon ou an voye bay chofè yo! ✅");
                    toast.info("Chofè yo pral ba w pri pa yo kounye a. Al nan Dashboard poun fini.", { duration: 6000 });
                    setTimeout(() => onClose(), 2000);
                }
            }
        } catch (error) {
            console.error("Order creation error:", error);
            
            let errorMsg = "Erè nan kreye kòmand la.";
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (Array.isArray(detail)) {
                    // Extract message from Pydantic error array
                    errorMsg = detail[0]?.msg || JSON.stringify(detail);
                } else {
                    errorMsg = detail;
                }
            } else if (error.message) {
                errorMsg = error.message;
            }
            
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const isAutoCalculated = !!(coords && deliveryType === 'delivery');
    const productSubtotalWithSystemFee = Math.ceil((product.price_htg * qty) * (paymentMethod === 'bvgpay' ? 1.05 : 1.08));
    const finalTotal = deliveryType === 'delivery' ? productSubtotalWithSystemFee + (parseFloat(proposedFee) || 0) : productSubtotalWithSystemFee;

    return (
        <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-[500px] p-0 rounded-[45px] overflow-hidden border-none shadow-[0_0_100px_rgba(35,47,62,0.2)] bg-white">
                <DialogHeader className="bg-[#232F3E] p-8 text-white relative">
                    <DialogClose className="absolute top-4 right-4 text-white hover:text-[#FF9900] transition-colors outline-none z-[110]">
                        <X className="w-8 h-8 pointer-events-none" />
                    </DialogClose>
                    <DialogTitle className="text-2xl font-black italic tracking-tighter uppercase leading-none flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#FF9900] rounded-2xl flex items-center justify-center text-black">
                            <ShoppingCart className="w-6 h-6" />
                        </div>
                        Achte <span className="text-[#FF9900]">Atik</span> sa a
                    </DialogTitle>
                </DialogHeader>

                <div className="p-8 space-y-8 overflow-y-auto max-h-[80vh] custom-scrollbar">
                    {/* Product Summary */}
                    <div className="bg-gray-50 rounded-[35px] p-6 border border-gray-100 flex flex-col gap-5">
                        <div className="flex gap-6">
                            <div className="flex flex-col gap-2 shrink-0">
                                <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center border-2 border-gray-100 overflow-hidden shadow-inner cursor-pointer" onClick={() => window.open(productImage, '_blank')}>
                                    {productImage ? (
                                        <img
                                            src={productImage}
                                            alt={productName}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <ShoppingCart className="w-8 h-8 text-gray-200" />
                                    )}
                                </div>
                                {validImages.length > 1 && (
                                    <div className="flex gap-2 mx-auto">
                                        {validImages.map((img, idx) => (
                                            <button 
                                                key={idx} 
                                                onClick={() => setSelectedImageIdx(idx)}
                                                className={`w-7 h-7 rounded-lg overflow-hidden border-2 transition-all ${selectedImageIdx === idx ? 'border-[#FF9900] scale-110 shadow-md' : 'border-gray-200 hover:border-gray-400 opacity-60 hover:opacity-100'}`}
                                            >
                                                <img src={img} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-black text-xl text-gray-900 leading-tight mb-2 uppercase tracking-tighter italic whitespace-normal line-clamp-2">{productName}</h3>
                                <div className="flex items-center gap-4">
                                    <span className="text-2xl font-black text-[#FF9900] leading-none italic">{product.price_htg.toLocaleString()} HTG</span>
                                    <div className="text-[10px] font-black uppercase text-gray-400 bg-gray-200/50 px-2 py-1 rounded-lg">Verifye</div>
                                </div>
                            </div>
                        </div>
                        {product.description && (
                            <div className="bg-white p-4 rounded-2xl border border-gray-100">
                                <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Deskripsyon Atik la</h4>
                                <p className="text-xs text-gray-600 font-medium leading-relaxed max-h-[80px] overflow-y-auto custom-scrollbar pr-2">{product.description}</p>
                            </div>
                        )}
                    </div>

                    {/* Social Share */}
                    <div className="space-y-4">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">PATAJE PWODWI SA A</p>
                        <div className="flex gap-4">
                            <Button 
                                variant="outline" 
                                onClick={() => handleShare("whatsapp")}
                                className="flex-1 h-16 rounded-2xl border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300 transition-all shadow-sm font-black uppercase tracking-tighter italic"
                            >
                                <MessageCircle className="w-5 h-5 mr-3" /> WhatsApp
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => handleShare("facebook")}
                                className="flex-1 h-16 rounded-2xl border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all shadow-sm font-black uppercase tracking-tighter italic"
                            >
                                <Facebook className="w-5 h-5 mr-3" /> Facebook
                            </Button>
                        </div>
                    </div>

                    {/* Quantity and Delivery Options */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">KANTITE</Label>
                            <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-2xl border border-gray-100">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => setQty(Math.max(1, qty - 1))}
                                    className="w-10 h-10 rounded-xl font-black text-xl hover:bg-white hover:shadow-sm"
                                >-</Button>
                                <span className="w-8 text-center font-black text-xl italic">{qty}</span>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => setQty(qty + 1)}
                                    className="w-10 h-10 rounded-xl font-black text-xl hover:bg-white hover:shadow-sm"
                                >+</Button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">OPSYON LIVREZON</Label>
                                <button
                                    type="button"
                                    onClick={() => setShowDriversList(!showDriversList)}
                                    className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-[11px] font-black rounded-full flex items-center gap-1.5 transition-all shadow-sm active:scale-95 cursor-pointer"
                                >
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    <span>{onlineDrivers.length} {onlineDrivers.length === 1 ? 'Chofè' : 'Chofè'} An Liy</span>
                                </button>
                            </div>

                            {/* EXPANDABLE ONLINE DRIVERS LIST */}
                            {showDriversList && (
                                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 p-4 rounded-2xl shadow-xl text-white animate-in fade-in zoom-in-95 duration-200">
                                    <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-white/10">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                                            <h3 className="font-black text-xs uppercase tracking-wider text-emerald-400">
                                                CHOFÈ KI AN LIY KOUNYE A ({onlineDrivers.length})
                                            </h3>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => setShowDriversList(false)}
                                            className="text-slate-400 hover:text-white text-xs font-black px-2 py-0.5 rounded-lg bg-white/5 cursor-pointer"
                                        >
                                            Fèmen ✕
                                        </button>
                                    </div>

                                    {onlineDrivers.length === 0 ? (
                                        <p className="text-xs text-slate-400 text-center py-3 font-medium">
                                            Pa gen chofè an liy nan moman an. N ap notifye yo kou w fè kòmand lan!
                                        </p>
                                    ) : (
                                        <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                                            {onlineDrivers.map(d => {
                                                const zone = d.commune || d.zone || d.department || 'Pòtoprens';
                                                const isMoto = (d.vehicle_type || '').toLowerCase().includes('moto');
                                                return (
                                                    <div key={d.id || d.phone} className="flex items-center justify-between p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition-all">
                                                        <div className="flex items-center gap-3">
                                                            {d.profile_photo ? (
                                                                <img src={d.profile_photo} alt={d.fullname || d.name} className="w-10 h-10 rounded-full object-cover border border-emerald-500/40" />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-black text-sm">
                                                                    {(d.fullname || d.name || 'C').slice(0, 1).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <p className="font-black text-xs text-white">{d.fullname || d.name || 'Chofè'}</p>
                                                                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                                        {isMoto ? '🛵 Moto' : '🚗 Machin'}
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] font-bold text-amber-400 flex items-center gap-1 mt-0.5">
                                                                    <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                                                    <span>Zòn: {zone}</span>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <span className="text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full border border-emerald-500/20 flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> AN LIY
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-4">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setDeliveryType("pickup")}
                                    className={`flex-1 h-16 rounded-2xl border-2 transition-all font-black uppercase tracking-tighter italic ${deliveryType === 'pickup' ? 'border-[#FF9900] bg-orange-50 text-[#FF9900]' : 'border-gray-100 bg-gray-50 text-gray-400'}`}
                                >
                                    <ShoppingCart className="w-5 h-5 mr-3" /> Pickup
                                </Button>
                                <Button 
                                    variant="outline"
                                    onClick={() => setDeliveryType("delivery")}
                                    className={`flex-1 h-16 rounded-2xl border-2 transition-all font-black uppercase tracking-tighter italic ${deliveryType === 'delivery' ? 'border-[#FF9900] bg-orange-50 text-[#FF9900]' : 'border-gray-100 bg-gray-50 text-gray-400'}`}
                                >
                                    <MapPin className="w-5 h-5 mr-3" /> Livrezon
                                </Button>
                            </div>
                        </div>

                        {deliveryType === "delivery" && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300 bg-orange-50/50 p-6 rounded-[32px] border border-orange-100">
                                {/* Vehicle Selector */}
                                <div className="space-y-1.5">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-gray-500">CHWAZI TIP VEYIKIL LIVREZON</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleVehicleChange("moto")}
                                            className={`p-3 rounded-2xl border-2 font-black text-xs uppercase flex items-center justify-center gap-2 transition-all ${
                                                deliveryVehicle === "moto"
                                                ? "border-[#FF9900] bg-orange-100/80 text-[#FF9900] shadow-sm"
                                                : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"
                                            }`}
                                        >
                                            <span>🏍️ Moto</span>
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FF9900]/20 text-[#FF9900] font-black">25 G/KM</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleVehicleChange("machine")}
                                            className={`p-3 rounded-2xl border-2 font-black text-xs uppercase flex items-center justify-center gap-2 transition-all ${
                                                deliveryVehicle === "machine"
                                                ? "border-cyan-500 bg-cyan-50 text-cyan-900 shadow-sm"
                                                : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"
                                            }`}
                                        >
                                            <span>🚗 Machin</span>
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-700 font-black">50 G/KM</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-[#FF9900]">ADRÈS LIVREZON</Label>
                                    <Input
                                        placeholder="Kote n'ap pote l la... (Egz: #14 Delma 33)"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        className="h-16 rounded-2xl border-2 border-white bg-white font-bold shadow-sm"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleGetLocation}
                                        className={`w-full h-12 rounded-xl border-dashed border-2 transition-all font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 ${coords ? 'border-green-300 bg-green-50 text-green-700' : 'border-[#FF9900]/30 text-[#FF9900] hover:bg-white'}`}
                                    >
                                        {coords ? <CheckCircle className="w-4 h-4" /> : <Navigation className="w-4 h-4" />}
                                        {coords ? "Lokalizasyon anrejistre (GPS) ✅" : "Voye pozisyon m pou mezire distans la (GPS)"}
                                    </Button>
                                </div>

                                <div className="space-y-3 pt-2">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-[#FF9900]">PRI LIVREZON (Kalkile pa KM)</Label>
                                    <div className="relative">
                                        <div className="h-16 rounded-2xl border-2 border-white bg-gray-50 font-black text-xl italic shadow-sm pl-6 flex items-center text-[#232F3E]">
                                            {proposedFee ? `${parseFloat(proposedFee).toLocaleString()}` : "Touche GPS pou mezire distans"}
                                        </div>
                                        {proposedFee && <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-400 uppercase tracking-widest">HTG</span>}
                                    </div>
                                    <p className="text-[10px] font-bold text-gray-500 flex items-center justify-between">
                                        <span>📍 Distans: {calculatedKm ? `${calculatedKm.toFixed(1)} KM` : 'Klike GPS pou distans la'}</span>
                                        <span className="font-black text-amber-700">
                                            {deliveryVehicle === 'machine' ? '50 GDS / KM (Machin)' : '25 GDS / KM (Moto)'}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Achte pou yon moun */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between bg-gray-50 p-4 rounded-2xl border border-gray-200">
                            <div>
                                <Label className="text-xs font-black uppercase tracking-widest text-[#232F3E]">ACHTE POU YON LÒT MOUN?</Label>
                                <p className="text-[10px] text-gray-500 font-bold mt-0.5">Voye pwodwi a bay yon fanmi oswa zanmi</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={buyForSomeone} onChange={() => setBuyForSomeone(!buyForSomeone)} />
                                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-4 peer-focus:ring-[#FF9900]/30 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF9900]"></div>
                            </label>
                        </div>
                        
                        {buyForSomeone && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300 bg-blue-50/50 p-6 rounded-[32px] border border-blue-100">
                                <div className="space-y-3">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-blue-800">NON MOUN K'AP RESEVWA L LA</Label>
                                    <Input
                                        placeholder="Ex: Jean Baptiste"
                                        value={recipientName}
                                        onChange={(e) => setRecipientName(e.target.value)}
                                        className="h-14 rounded-2xl border-2 border-white bg-white font-bold shadow-sm"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-blue-800">TELEFÒN MOUN NAN</Label>
                                    <Input
                                        type="tel"
                                        placeholder="Ex: 34567890"
                                        value={recipientPhone}
                                        onChange={(e) => setRecipientPhone(e.target.value)}
                                        className="h-14 rounded-2xl border-2 border-white bg-white font-bold shadow-sm"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Section */}
                    <div className="bg-gradient-to-br from-[#232F3E] to-black rounded-[40px] p-8 text-white shadow-2xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF9900]/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-[#FF9900]/20 transition-all duration-700"></div>
                        <div className="flex justify-between items-start mb-6">
                            <h4 className="flex items-center gap-3 text-xl font-black uppercase tracking-tighter italic">
                                {deliveryType === 'pickup' || deliveryType === 'delivery' ? (
                                    <><CreditCard className="w-6 h-6 text-[#FF9900]" /> Peye Kounye a</>
                                ) : (
                                    <><Navigation className="w-6 h-6 text-[#FF9900]" /> Negosyasyon</>
                                )}
                            </h4>
                            <div className="text-right">
                                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                    {paymentMethod === 'bvgpay' ? 'TOTAL (Pwodwi + 5%)' : 'TOTAL (Pwodwi + 8%)'} {deliveryType === 'delivery' && '+ TRANSPÒ'}
                                </div>
                                <div className="text-2xl font-black text-[#FF9900] italic">
                                    {finalTotal.toLocaleString()} HTG
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 relative z-10 mb-6">
                             <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">CHWAZI MWAYEN PEMAN OU</Label>
                             <div className="flex gap-4">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setPaymentMethod("moncash")}
                                    className={`flex-1 h-16 rounded-2xl border-2 transition-all font-black uppercase tracking-widest ${paymentMethod === 'moncash' ? 'border-[#FF9900] bg-[#FF9900]/10 text-[#FF9900]' : 'border-white/10 bg-white/5 text-gray-400'}`}
                                >
                                    MonCash
                                </Button>
                                <Button 
                                    variant="outline" 
                                    onClick={() => setPaymentMethod("bvgpay")}
                                    className={`flex-1 h-16 relative rounded-2xl border-2 transition-all font-black uppercase tracking-widest ${paymentMethod === 'bvgpay' ? 'border-indigo-500 bg-indigo-500/20 text-indigo-400' : 'border-white/10 bg-white/5 text-gray-400'}`}
                                >
                                    <div className="absolute -top-3 -right-2 bg-red-500 text-white text-[8px] px-2 py-1 rounded-full animate-pulse">-3% FEE</div>
                                    BVGPAY
                                </Button>
                             </div>
                             {paymentMethod === "bvgpay" && token && bvgpayBalance !== null && (
                                <p className="text-xs text-indigo-300 font-bold">Balans ou: <span className="text-white bg-indigo-900/50 px-2 py-1 rounded-md">{bvgpayBalance.toLocaleString()} HTG</span></p>
                             )}
                        </div>
                        
                        <div className="space-y-6 relative z-10">
                            {/* Always ask for phone number */}
                            <div className="space-y-3">
                                <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    {paymentMethod === "moncash" ? "Nimewo MonCash (8 Chif)" : "Nimewo Telefòn Ou (8 Chif)"}
                                </Label>
                                <div className="flex shadow-2xl rounded-2xl overflow-hidden focus-within:ring-4 ring-[#FF9900]/30 transition-all">
                                    <div className="bg-white/10 px-5 flex items-center justify-center border-r border-white/5 font-black text-[#FF9900] backdrop-blur-md">+509</div>
                                    <Input
                                        type="tel"
                                        placeholder="3xxx xxxx"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                        className="bg-white/5 border-none h-16 text-xl font-black placeholder:text-gray-600 focus-visible:ring-0 rounded-none backdrop-blur-md text-white"
                                    />
                                </div>
                            </div>

                            {paymentMethod === "bvgpay" && (
                                !token ? (
                                    <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4 text-center">
                                        <p className="text-xs text-red-200 mb-3 font-bold">Ou dwe konekte pou w' peye ak Bous BVGPAY ou a.</p>
                                        <Button asChild variant="outline" className="h-10 text-xs border-white bg-white/10 text-white hover:bg-white hover:text-black">
                                            <a href="/linkamazon/login" target="_blank" rel="noreferrer">Konekte sou Kont Ou</a>
                                        </Button>
                                    </div>
                                ) : bvgpayBalance < Math.ceil((product.price_htg * qty) * 1.05) ? (
                                    <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4 text-center">
                                        <p className="text-xs text-red-200 mb-3 font-bold">Lajan ki sou bous ou a pa ase pou kòmand sa.</p>
                                        <Button asChild variant="outline" className="h-10 text-xs border-indigo-500 bg-indigo-500 text-white hover:bg-indigo-600">
                                            <a href="/bvgpay/wallet" target="_blank" rel="noreferrer">Rechaje Bous La</a>
                                        </Button>
                                    </div>
                                ) : null
                            )}

                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={handleCreateOrder}
                                    disabled={loading || (paymentMethod === "bvgpay" && (!token || bvgpayBalance < finalTotal))}
                                    className={`w-full h-16 ${paymentMethod === 'bvgpay' ? 'bg-indigo-500 hover:bg-indigo-400 shadow-indigo-500/20 text-white' : 'bg-[#FF9900] hover:bg-[#FF9900]/90 shadow-orange-500/20 text-black'} font-black text-xl uppercase tracking-tighter italic rounded-[25px] shadow-2xl active:scale-95 transition-all`}
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-3"><Loader2 className="w-6 h-6 animate-spin" /> AP TRAVAY...</span>
                                    ) : deliveryType === 'pickup' || deliveryType === 'delivery' ? (
                                        "Peye Kounye a"
                                    ) : (
                                        "MANDE LIVREZON"
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        addToCart({ ...product, qty });
                                        toast.success("Ajoute nan panye ak siksè!");
                                        onClose();
                                    }}
                                    className="w-full h-14 bg-white/5 border-white/10 hover:bg-white/10 text-white font-black text-lg uppercase tracking-tighter italic rounded-[25px] transition-all"
                                >
                                    <ShoppingCart className="w-5 h-5 mr-2" /> Ajoute nan Panye
                                </Button>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center font-bold uppercase tracking-widest opacity-60">
                                {paymentMethod === "bvgpay" ? "Peman Imidya ak Bous BVGPAY Ou" : "Peman Sekirize pa MonCash"}
                            </p>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
